import type { CSSProperties, FC, FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useFetcher,
  useLocation,
  useNavigate,
  useRevalidator,
  useRouteLoaderData,
  useSearchParams,
} from '@remix-run/react';
import { SignupGDPRCompliance } from '@shared/dream-components';

import useGDPRTranslations from '~/components/DreamBuilder/nodes/hooks/useGDPRTranslations';
import SignupAction from '~/components/SignupAction';
import { useEnsureCsrfToken } from '~/context/CsrfContext';
import { readStoredEmail } from '~/hooks/useEmail';
import type { Community } from '~/interfaces/community';
import { getSSORedirectUrl } from '~/pages/upgrade/utils/getSSORedirectUrl';
import type { RootLoaderData } from '~/root';
import isValidEmail from '~/utils/isValidEmail';
import { NectarPageBlock, nectarPageBlock, toNectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';
import { NECTAR_PAGE_BLOCK_GET_PARAM_NAME } from '~/utils/nectarTrackingFields';
import { getStytchTelemetryId } from '~/utils/stytch';

import { CommunityTermsConsent } from './ui/CommunityTermsConsent';
import { PillButton, pillButtonInteraction } from './ui/PillButton';
import { PillTextInput } from './ui/PillTextInput';

import type { CommunityJoinModalState } from '../_hooks/useCommunityMembership';
import { getCommunityDescription } from '../_utils/getCommunityDescription';
import { getFullCommunityName } from '../_utils/getFullCommunityName';
import { CommunityAuthModal } from './CommunityAuthModal';

export type CommunityAuthMode = 'join' | 'login';

const AUTH_COPY = {
  join: {
    titleKey: 'community.join.title',
    descriptionKey: 'community.join.email_prompt',
    closeKey: 'community.join.close',
    footerPromptKey: 'community.join.already_member',
    footerCtaKey: 'shared.cta.sign_in',
    formId: 'community-signup-form',
  },
  login: {
    titleKey: 'community.login.title',
    descriptionKey: 'community.login.email_prompt',
    closeKey: 'community.login.close',
    footerPromptKey: 'community.login.not_a_member',
    footerCtaKey: 'community.join.cta',
    formId: 'community-signin-form',
  },
} as const;

// A guest who just paid is sent to sign in to verify the email they checked out with.
const VERIFY_COPY = {
  ...AUTH_COPY.login,
  titleKey: 'community.login.verify_title',
  descriptionKey: 'community.login.verify_email_prompt',
} as const;

interface CommunityAuthFormProps {
  mode: CommunityAuthMode;
  isOpen: boolean;
  onClose: () => void;
  onSwitchMode: () => void;
  isLoggedIn: boolean;
  publicationName: string;
  community:
    | (Pick<Community, 'name' | 'description'> & Partial<Pick<Community, 'gated' | 'community_accessible'>>)
    | null;
  publicationId?: string;
  redirectTo: string;
}

export const CommunityAuthForm: FC<CommunityAuthFormProps> = ({
  mode,
  isOpen,
  onClose,
  onSwitchMode,
  isLoggedIn,
  publicationName,
  community,
  publicationId,
  redirectTo,
}) => {
  const { t } = useTranslation();
  const ensureCsrfToken = useEnsureCsrfToken();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const gdprTranslations = useGDPRTranslations();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const joinDestination = (location.state as CommunityJoinModalState | null)?.joinDestination;
  const membershipFetcher = useFetcher<{ ok?: boolean; message?: string }>();
  const [email, setEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [stytchTelemetryId, setStytchTelemetryId] = useState<string | undefined>(undefined);
  const hasHandledJoinSuccessRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRequestingCode, setIsRequestingCode] = useState(false);

  const isUpgradeReturn = mode === 'login' && searchParams.get('upgrade_return') === '1';
  const copy = isUpgradeReturn ? VERIFY_COPY : AUTH_COPY[mode];
  const communityName = getFullCommunityName(community);
  const canContinue = isValidEmail(email.trim()) && Boolean(publicationId);
  const isJoining = membershipFetcher.state !== 'idle';

  const isGatedForViewer = Boolean(community?.gated && !community.community_accessible);
  // Gated communities skip the auto-join, because a refused auto-join can't tell the member why. The join button can.
  const canAutoJoinCommunity = !isGatedForViewer;

  const { terms_of_service_enabled, terms_of_service, terms_of_service_text } = site.viewable_site_version.settings;

  // Logged-in members open the join modal to confirm membership; that view shows
  // the community's own description (falling back to the default blurb) and its
  // own heading. Every other case uses the mode's standard copy.
  const isMemberJoinView = isLoggedIn && mode === 'join';
  const title = isMemberJoinView ? communityName : t(copy.titleKey, { communityName });
  const description = isMemberJoinView ? getCommunityDescription(community, t) : t(copy.descriptionKey);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setTermsAccepted(false);
      setGdprAccepted(false);
      setErrorMessage(null);
      setIsRequestingCode(false);
      hasHandledJoinSuccessRef.current = false;
    }
  }, [isOpen]);

  // Read on open, not at mount: the checkout stores the email after this form is already mounted.
  useEffect(() => {
    if (!isOpen || mode !== 'login') return;
    const storedEmail = readStoredEmail();
    if (storedEmail) setEmail(storedEmail);
  }, [isOpen, mode]);

  useEffect(() => {
    if (membershipFetcher.state !== 'idle') {
      hasHandledJoinSuccessRef.current = false;
      return;
    }
    if (!membershipFetcher.data) return;

    if (membershipFetcher.data.ok) {
      if (hasHandledJoinSuccessRef.current) return;
      hasHandledJoinSuccessRef.current = true;
      if (joinDestination) {
        navigate(joinDestination);
        return;
      }
      onClose();
      revalidator.revalidate();
      return;
    }

    setErrorMessage(membershipFetcher.data.message ?? t('errors.default'));
  }, [membershipFetcher.state, membershipFetcher.data, joinDestination, navigate, onClose, revalidator, t]);

  useEffect(() => {
    if (isOpen && !isLoggedIn) {
      getStytchTelemetryId().then(setStytchTelemetryId);
    }
  }, [isOpen, isLoggedIn]);

  const handleJoinAsLoggedInMember = async () => {
    setErrorMessage(null);
    const formData = new FormData();
    formData.append('intent', 'join');
    const csrfToken = await ensureCsrfToken();
    if (csrfToken) formData.append('csrf', csrfToken);
    formData.append('community_terms_accepted', 'true');
    const telemetryId = await getStytchTelemetryId();
    if (telemetryId) {
      formData.append('stytch_telemetry_id', telemetryId);
    }
    withNectarTrackingParams(formData, toNectarPageBlock(searchParams.get(NECTAR_PAGE_BLOCK_GET_PARAM_NAME)));
    membershipFetcher.submit(formData, { method: 'post', action: '/community_membership' });
  };

  // Sign-in only requests a code; the response is identical whether or not the email is a member's.
  const handleRequestCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!publicationId) return;
    setErrorMessage(null);
    setIsRequestingCode(true);
    try {
      const { redirect_url } = await getSSORedirectUrl({
        publication_id: publicationId,
        email: email.trim(),
        redirect_to: redirectTo,
      });
      if (!redirect_url) throw new Error('sso_redirect returned no url');
      navigate(redirect_url);
    } catch {
      setErrorMessage(t('errors.default'));
      setIsRequestingCode(false);
    }
  };

  const footer = isLoggedIn ? undefined : (
    <p className="text-center text-[13px] leading-6 text-black/50">
      {t(copy.footerPromptKey)}{' '}
      <button
        type="button"
        onClick={onSwitchMode}
        className="underline transition-colors duration-150 hover:text-black"
      >
        {t(copy.footerCtaKey)}
      </button>
    </p>
  );

  return (
    <CommunityAuthModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      closeLabel={t(copy.closeKey)}
      publicationName={publicationName}
      footer={footer}
    >
      {isLoggedIn ? (
        <div className="flex w-full flex-col gap-4">
          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
          <CommunityTermsConsent checked={termsAccepted} onChange={setTermsAccepted} />
          <PillButton
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleJoinAsLoggedInMember}
            disabled={isJoining || !termsAccepted}
            className={pillButtonInteraction}
            {...nectarPageBlock(NectarPageBlock.CommunityJoinModal)}
          >
            {isJoining ? t('shared.actions.submitting') : t('community.join.cta')}
          </PillButton>
        </div>
      ) : mode === 'login' ? (
        <form id={copy.formId} onSubmit={handleRequestCode} className="flex w-full flex-col gap-4">
          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
          <PillTextInput
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t('subscribe.email_placeholder')}
          />
          <PillButton
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canContinue || isRequestingCode}
            className={pillButtonInteraction}
          >
            {isRequestingCode ? t('shared.actions.submitting') : t('shared.cta.continue')}
          </PillButton>
        </form>
      ) : (
        <SignupAction id={copy.formId} shouldSkipRedirectsAfterSuccess redirectUrl={redirectTo} disableSparkLoop>
          {({ emailFieldName, emailFieldType }) => (
            <div className="flex w-full flex-col gap-4">
              <input type="hidden" value="true" name="send_otp_after_subscribe" />
              <input type="hidden" value={canAutoJoinCommunity ? 'true' : 'false'} name="join_community" />
              <input type="hidden" name="stytch_telemetry_id" value={stytchTelemetryId ?? ''} />
              <PillTextInput
                type={emailFieldType}
                name={emailFieldName}
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('subscribe.email_placeholder')}
              />
              {terms_of_service_enabled && (
                <div
                  className="w-full"
                  onChange={(event) => {
                    const target = event.target as HTMLInputElement;
                    if (target.name === 'tos_consent') setGdprAccepted(target.checked);
                  }}
                  style={
                    { '--gdpr-text-color': 'rgba(0, 0, 0, 0.5)', '--gdpr-checkbox-color': '#000000' } as CSSProperties
                  }
                >
                  <SignupGDPRCompliance
                    hasCustomTermsOfService={terms_of_service}
                    customTermsOfServiceText={terms_of_service_text || undefined}
                    defaultTOSConsentValue={false}
                    gdprTranslations={gdprTranslations}
                  />
                </div>
              )}
              <CommunityTermsConsent checked={termsAccepted} onChange={setTermsAccepted} />
              <PillButton
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={!canContinue || !termsAccepted || (terms_of_service_enabled && !gdprAccepted)}
                className={pillButtonInteraction}
                {...nectarPageBlock(NectarPageBlock.CommunityJoinModal)}
              >
                {t('shared.cta.continue')}
              </PillButton>
            </div>
          )}
        </SignupAction>
      )}
    </CommunityAuthModal>
  );
};
